/**
 * InfographicHero — reusable band that renders a WebsiteTech infographic
 * inside a page hero. Handles lazy loading, error fallback, and an optional
 * "See gallery" hint underneath.
 *
 *   <InfographicHero slug="local-visibility-engine" />
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { ImageOff, Sparkles } from "lucide-react";
import { findInfographic } from "@/data/infographics";

export default function InfographicHero({ slug, hint = true, className = "" }) {
  const info = findInfographic(slug);
  const [failed, setFailed] = useState(false);
  if (!info) return null;
  return (
    <div
      className={`overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm ${className}`}
      data-testid={`infographic-${slug}`}
    >
      {!failed ? (
        <img
          src={info.url}
          alt={`${info.title} — ${info.tagline}`}
          loading="lazy"
          onError={() => setFailed(true)}
          className="block h-auto w-full"
        />
      ) : (
        <div className="flex items-center justify-center gap-2 p-6 text-sm text-slate-500">
          <ImageOff className="h-4 w-4" /> Infographic unavailable
        </div>
      )}
      {hint && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 bg-slate-50 px-4 py-2 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1">
            <Sparkles className="h-3 w-3 text-orange-500" />
            {info.title}
          </span>
          <Link
            to="/features/gallery"
            className="font-semibold text-orange-600 hover:underline"
            data-testid={`infographic-gallery-link-${slug}`}
          >
            See all feature infographics →
          </Link>
        </div>
      )}
    </div>
  );
}
